import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IUserStats, getUserStats } from '../../services/api-graphql-calls';
import { Container, Badge, Tooltip, TooltipRow, TooltipDivider } from './styles';

interface ICustomerTagProps {
  userId: string;
  direction?: 'horizontal' | 'vertical';
  tooltipLayout?: 'row' | 'column';
}

interface ITooltipPos {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

const formatDate = (value: string | null): string | null => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('pt-BR');
};

const formatNumber = (value: number | null): string | null => {
  if (value === null || value === undefined) return null;
  return String(value);
};

const formatRating = (value: number | null): string | null => {
  if (value === null || value === undefined) return null;
  return value.toFixed(1);
};

const TOOLTIP_HEIGHT_ESTIMATE = 300;
const TOOLTIP_OFFSET = 8;

const CustomerTag: React.FC<ICustomerTagProps> = ({
  userId,
  direction = 'horizontal',
  tooltipLayout = 'column',
}) => {
  const badgeRef = useRef<HTMLSpanElement>(null);
  const [stats, setStats] = useState<IUserStats | null>(null);
  const [error, setError] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<ITooltipPos | null>(null);

  useEffect(() => {
    getUserStats(userId)
      .then(setStats)
      .catch((err) => {
        console.error('[CustomerTag] Erro ao buscar stats do usuário:', err);
        setError(true);
      });
  }, [userId]);

  const handleMouseEnter = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const placement = spaceAbove >= TOOLTIP_HEIGHT_ESTIMATE ? 'top' : 'bottom';
    setTooltipPos({
      top: placement === 'top'
        ? rect.top + window.scrollY - TOOLTIP_OFFSET
        : rect.bottom + window.scrollY + TOOLTIP_OFFSET,
      left: rect.left + window.scrollX + rect.width / 2,
      placement,
    });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  if (!stats || error) return null;

  const isFirstPurchase = (stats.total_completed ?? 0) === 0;

  const rows: { label: string; value: string | null }[] = [
    { label: 'Agendamentos', value: formatNumber(stats.total_appointments) },
    { label: 'Concluídos', value: formatNumber(stats.total_completed) },
    { label: 'Cancelados', value: formatNumber(stats.total_canceled) },
    { label: 'Banhos/tosas', value: formatNumber(stats.total_grooming) },
    { label: 'Consultas vet.', value: formatNumber(stats.total_vet) },
    { label: 'Vacinas', value: formatNumber(stats.total_vaccines) },
    { label: 'Pets', value: formatNumber(stats.total_pets) },
    { label: 'Avaliações', value: formatNumber(stats.total_ratings) },
    { label: 'Nota média', value: formatRating(stats.rating_average) },
    { label: 'Nota máx.', value: formatRating(stats.rating_max) },
    { label: 'Nota mín.', value: formatRating(stats.rating_min) },
    { label: 'Serviço mais usado', value: stats.most_used_service_name },
    { label: 'Último agend.', value: formatDate(stats.last_appointment_date) },
    { label: 'Último banho/tosa', value: formatDate(stats.last_grooming_date) },
    { label: 'Última vacina', value: formatDate(stats.last_vaccine_date) },
    { label: 'Última consulta', value: formatDate(stats.last_vet_date) },
  ];

  const visibleRows = rows.filter((row) => row.value !== null);

  return (
    <Container>
      <Badge
        ref={badgeRef}
        isFirstPurchase={isFirstPurchase}
        $direction={direction}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isFirstPurchase ? 'Primeira vez' : 'Cliente'}
      </Badge>
      {tooltipPos && visibleRows.length > 0 && ReactDOM.createPortal(
        <Tooltip layout={tooltipLayout} $pos={tooltipPos}>
          {visibleRows.map((row, index) => (
            <React.Fragment key={row.label}>
              {tooltipLayout === 'column' && index > 0 && index % 3 === 0 && <TooltipDivider />}
              <TooltipRow layout={tooltipLayout}>
                <span>{row.label}:</span>
                <span>{row.value}</span>
              </TooltipRow>
            </React.Fragment>
          ))}
        </Tooltip>,
        document.body,
      )}
    </Container>
  );
};

export default CustomerTag;
